import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import runtimeConfig from '../config/runtime.config.js'

export const useConfigStore = defineStore('config', () => {
  // Get configuration from Vite's define constants
  const viteConfig = typeof __APP_CONFIG__ !== 'undefined' ? __APP_CONFIG__ : {}
  
  // Default configuration
  const defaultConfig = {
    website: {
      title: typeof __WEBSITE_TITLE__ !== 'undefined' ? __WEBSITE_TITLE__ : 'BTMS',
      subtitle: 'Transport Management System with GPS Tracking',
      icon: '/media/tracking.png',
      logo: typeof __WEBSITE_LOGO__ !== 'undefined' ? __WEBSITE_LOGO__ : 'BTMS',
      colors: {
        primary: typeof __PRIMARY_COLOR__ !== 'undefined' ? __PRIMARY_COLOR__ : '#03193b',
        secondary: typeof __SECONDARY_COLOR__ !== 'undefined' ? __SECONDARY_COLOR__ : '#1F1F1F',
        accent: typeof __ACCENT_COLOR__ !== 'undefined' ? __ACCENT_COLOR__ : '#D98324'
      }
    },
    app: {
      name: typeof __APP_NAME__ !== 'undefined' ? __APP_NAME__ : 'BTMS',
      version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0',
      description: 'Transport Management System with GPS Tracking capabilities'
    }
  }

  // Reactive configuration state
  const config = reactive({ ...defaultConfig })
  const isLoading = ref(false)
  const error = ref(null)

  // Load configuration from multiple sources
  async function loadConfig() {
    isLoading.value = true
    error.value = null
    
    try {
      // Get runtime configuration (includes localStorage)
      const runtimeConfigData = runtimeConfig.getAll()
      
      // Merge configurations: default -> vite -> runtime
      const mergedConfig = {
        ...defaultConfig,
        ...viteConfig,
        ...runtimeConfigData,
        website: {
          ...defaultConfig.website,
          ...viteConfig.website,
          ...runtimeConfigData.website,
          colors: {
            ...defaultConfig.website.colors,
            ...viteConfig.website?.colors,
            ...runtimeConfigData.website?.colors
          }
        },
        app: {
          ...defaultConfig.app,
          ...viteConfig.app,
          ...runtimeConfigData.app
        }
      }
      
      // Update reactive config
      Object.assign(config, mergedConfig)
      
      // Apply CSS custom properties for colors
      applyColors()
      
    } catch (err) {
      error.value = err.message
      console.warn('Failed to load configuration, using defaults:', err)
    } finally {
      isLoading.value = false
    }
  }

  // Apply colors to CSS custom properties
  function applyColors() {
    const root = document.documentElement
    root.style.setProperty('--primary-color', config.website.colors.primary)
    root.style.setProperty('--secondary-color', config.website.colors.secondary)
    root.style.setProperty('--accent-color', config.website.colors.accent)
    root.style.setProperty('--active-color', config.website.colors.accent)
  }

  // Update configuration
  function updateConfig(newConfig) {
    // Update runtime configuration
    runtimeConfig.update(newConfig)
    
    // Update reactive config
    Object.assign(config, {
      ...config,
      ...newConfig,
      website: {
        ...config.website,
        ...newConfig.website,
        colors: {
          ...config.website.colors,
          ...newConfig.website?.colors
        }
      },
      app: {
        ...config.app,
        ...newConfig.app
      }
    })
    
    // Apply colors immediately
    applyColors()
    
    // Update page meta
    runtimeConfig.updatePageMeta()
  }

  // Reset to default configuration
  function resetConfig() {
    // Reset runtime configuration
    runtimeConfig.reset()
    
    // Reset reactive config
    Object.assign(config, defaultConfig)
    
    // Apply colors immediately
    applyColors()
    
    // Update page meta
    runtimeConfig.updatePageMeta()
  }

  // Getters
  const websiteTitle = () => config.website.title
  const websiteSubtitle = () => config.website.subtitle
  const websiteIcon = () => config.website.icon
  const websiteLogo = () => config.website.logo
  const primaryColor = () => config.website.colors.primary
  const secondaryColor = () => config.website.colors.secondary
  const accentColor = () => config.website.colors.accent

  return {
    // State
    config,
    isLoading,
    error,
    
    // Actions
    loadConfig,
    updateConfig,
    resetConfig,
    applyColors,
    
    // Getters
    websiteTitle,
    websiteSubtitle,
    websiteIcon,
    websiteLogo,
    primaryColor,
    secondaryColor,
    accentColor
  }
})
